import {memo, ReactNode, useCallback, useMemo} from 'react';
import styled from 'styled-components';
import toast from '@/components/HotToast';
import {CloseIconComponent, PinIconComponent} from '@/components/Icons';
import {Typography} from '@/components/Typography';
import {MAP_LAYER_Z_INDEX} from '@/map/constants';
import {Marker} from '@/map/modules/Overlay';
import {Flexbox, FlexFullGrow, interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import {copyText} from '@/utils';
import {MarkerContentContainer} from '../MarkerContentContainer';
import ShadowSvg from './shadow.svg';

interface IProps {
  address: string;
  iconComponent?: ReactNode;
  hasShadow?: boolean;
  onClose?(): void;
  onCopy?(): void;
}

const copyTextClassName = 'copy-text';

export const LocationMarker = memo(
  ({
    address,
    iconComponent: IconComponent,
    hasShadow,
    onClose,
    onCopy,
    ...props
  }: IProps & naver.maps.MarkerOptions) => {
    const isMultiLine = useMemo(() => address?.length > 15, [address?.length]);

    const copy = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!onCopy || e.button > 0) {
          return;
        }

        const element = document.getElementsByClassName(copyTextClassName)?.[0];

        if (element) {
          const text = element.innerHTML;
          const success = text && copyText(text);

          if (success) {
            toast('주소가 복사되었습니다.');
          }
          onCopy();
        }
      },
      [onCopy]
    );

    const handleClose = useCallback(
      (e: React.MouseEvent<SVGSVGElement>) => {
        if (!onClose || e.button > 0) {
          return;
        }

        onClose();
      },
      [onClose]
    );

    return (
      <Marker
        draggable={false}
        clickable={false}
        zIndex={MAP_LAYER_Z_INDEX.LOCATION}
        {...props}
      >
        <MarkerContentContainer anchor={{y: -3}}>
          <Body isMultiLine={isMultiLine}>
            <Flexbox alignItems="flex-start">
              <Flexbox
                alignItems="flex-start"
                className={onCopy ? 'has-hover' : undefined}
                onMouseDown={copy}
              >
                {IconComponent ?? <LocationIcon color={palette.white} />}
                <Typography
                  variant="medium14"
                  fontWeight="700"
                  className={copyTextClassName}
                >
                  {address}
                </Typography>
              </Flexbox>
              <FlexFullGrow />
              {!!onClose && <Close onMouseDown={handleClose} />}
            </Flexbox>
          </Body>
          <Arrow>{hasShadow && <ArrowShadow />}</Arrow>
        </MarkerContentContainer>
      </Marker>
    );
  }
);

const Arrow = styled.div`
  position: absolute;
  bottom: 1px;
  left: 50%;
  margin-left: -4px;
  width: 8px;
  height: 0px;
  z-index: 0;
  &:before {
    content: '';
    display: block;
    position: absolute;
    left: 5px;
    top: 100%;
    width: 0;
    height: 0;
    border: 4px solid transparent;
    border-top-width: 8px;
    transform: translateX(-50%);
    border-top-color: ${palette.gray800};
  }
`;

const ArrowShadow = styled('img').attrs({
  src: ShadowSvg,
  alt: 'shadow',
})`
  position: absolute;
  left: 50%;
  top: 7px;
  transform: translateX(-50%);
`;

const LocationIcon = styled(PinIconComponent)`
  flex-shrink: 0;
  margin-right: 4px;
`;

const Body = styled('div').withConfig({
  shouldForwardProp: interceptProps(['isMultiLine']),
})<{
  isMultiLine: boolean;
}>`
  border-radius: 5px;
  padding: 8px;
  box-sizing: border-box;
  background-color: ${palette.gray800};
  color: ${palette.white};
  ${({isMultiLine}) =>
    isMultiLine
      ? `
    width: 196px;
  `
      : `
  white-space: nowrap;
  `}
  & > ${Flexbox} {
    width: 100%;
  }
  .has-hover {
    cursor: pointer;
    &:hover {
      ${Typography} {
        color: ${palette.blue100};
      }
      ${LocationIcon} {
        color: ${palette.blue100};
      }
    }
  }
`;

const Close = styled(CloseIconComponent).attrs({
  color: palette.white,
})`
  cursor: pointer;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  margin-left: 8px;
  flex-shrink: 0;
`;
